import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StudentListItem, PaymentAccount, StudentPaymentAccount, PaginationInfo } from '../../../types';
import { queryFilter, useQueryFilters } from '../../../hooks/useQueryFilters';
import { PaymentAccountService, StudentPaymentAccountService } from '../../../services/PaymentAccountService';
import { StudentService, StudentSubscriptionService } from '../../../services';
import { formatDate } from '../../../utils';
import { PAYMENT_SYSTEM_LABELS, PaymentSystem } from '../../../types/payment';
import { isStudentSubscriptionActive } from '../../../utils/subscriptionLifecycle';
import { maskCpf } from '../../../utils';
import { PaymentGatewayService } from '../../../services/PaymentGatewayService';
import type { ExternalCustomer } from '../../../services/PaymentGatewayService';
import DataTable from '../../../components/common/DataTable/DataTable';
import EmptyState from '../../../components/common/EmptyState/EmptyState';
import Loading from '../../../components/common/Loading/Loading';
import Button from '../../../components/common/Button/Button';
import FormInput from '../../../components/common/FormInput/FormInput';
import Pagination from '../../../components/common/Pagination/Pagination';
import PageSizeSelect from '../../../components/common/PageSizeSelect/PageSizeSelect';
import SearchFilterBar from '../../../components/common/SearchFilterBar/SearchFilterBar';
import ConfirmModal from '../../../components/common/ConfirmModal/ConfirmModal';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ToolbarRow from '../../../components/common/ToolbarRow/ToolbarRow';
import styles from './FinanceiroTab.module.css';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50] as const;
const FINANCE_FILTER_SCHEMA = {
  page: queryFilter.integer(1, { min: 1, param: 'financePage' }),
  pageSize: queryFilter.integer(10, { allowed: PAGE_SIZE_OPTIONS, param: 'financePageSize' }),
  search: queryFilter.string('', { param: 'financeSearch' }),
};
const INITIAL_PAGINATION: PaginationInfo = {
  page: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

const FinanceiroTab: React.FC = () => {
  const [selectedStudent, setSelectedStudent] = useState<StudentListItem | null>(null);

  return selectedStudent
    ? <StudentPaymentLinkView student={selectedStudent} onBack={() => setSelectedStudent(null)} />
    : <StudentSelectView onSelect={setSelectedStudent} />;
};

const StudentSelectView: React.FC<{ onSelect: (s: StudentListItem) => void }> = ({ onSelect }) => {
  const { filters, setFilter } = useQueryFilters(FINANCE_FILTER_SCHEMA, { pageKey: 'page' });
  const { page, pageSize, search: searchName } = filters;
  const [students, setStudents] = useState<StudentListItem[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const previousSearchRef = useRef(searchName);

  const loadStudents = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const response = await StudentService.listPaginated(page, pageSize, {
        searchName: searchName || undefined,
      });
      if (requestId !== requestIdRef.current) return;
      setStudents(response.data);
      setPagination(response.pagination);
    } catch (loadError) {
      if (requestId !== requestIdRef.current) return;
      setStudents([]);
      setError(loadError instanceof Error ? loadError.message : 'Erro ao carregar alunas');
      setPagination({
        ...INITIAL_PAGINATION,
        page,
        pageSize,
      });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, pageSize, searchName]);

  useEffect(() => {
    const searchChanged = previousSearchRef.current !== searchName;
    previousSearchRef.current = searchName;
    const timer = window.setTimeout(() => {
      void loadStudents();
    }, searchChanged ? 400 : 0);
    return () => window.clearTimeout(timer);
  }, [loadStudents, searchName]);

  return (
    <>
      <PageHeader title="Financeiro"/>

      <ToolbarRow>
        <SearchFilterBar
          searchValue={searchName}
          onSearchChange={(value) => setFilter('search', value)}
          searchPlaceholder="Pesquisar"
          loading={loading}
        />
      </ToolbarRow>

      <div>
        {error ? (
          <p className={styles.error}>{error}</p>
        ) : loading ? (
          <Loading />
        ) : students.length === 0 ? (
          <EmptyState message="Nenhuma aluna encontrada." />
        ) : (
          <DataTable
            headers={['Nome', 'CPF', 'E-mail', '']}
            gridTemplate="1fr 160px 1fr 120px"
            highlightTerm={searchName}
            borderedRows
            boldHeaders
          >
            {students.map((s) => (
              <DataTable.Row key={s.id}>
                <span className={styles['cell-name']}>{s.full_name}</span>
                <span>{s.cpf ? maskCpf(s.cpf) : '—'}</span>
                <span>{s.email ?? '—'}</span>
                <div className={styles['actions-cell']}>
                  <Button variant="primary" size="xs" onClick={() => onSelect(s)}>
                    Selecionar
                  </Button>
                </div>
              </DataTable.Row>
            ))}
          </DataTable>
        )}
      </div>

      {students.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={PAGE_SIZE_OPTIONS}
            onChange={(event) => setFilter('pageSize', Number.parseInt(event.target.value, 10))}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={(targetPage) => setFilter('page', targetPage, { resetPage: false })}
            />
          </div>
        </div>
      )}
    </>
  );
};

type JunctionWithAccount = StudentPaymentAccount & { payment_accounts: PaymentAccount };

const StudentPaymentLinkView: React.FC<{ student: StudentListItem; onBack: () => void }> = ({ student, onBack }) => {
  const [existingAccounts, setExistingAccounts] = useState<JunctionWithAccount[]>([]);
  const [existingLoading, setExistingLoading] = useState(true);
  const [junctionSubMap, setJunctionSubMap] = useState<Record<number, boolean>>({});
  const [junctionToUnlink, setJunctionToUnlink] = useState<JunctionWithAccount | null>(null);
  const [unlinking, setUnlinking] = useState(false);
  const [unlinkError, setUnlinkError] = useState<string | null>(null);

  const [selectedSystem, setSelectedSystem] = useState<PaymentSystem>('asaas');
  const [cpfInput, setCpfInput] = useState('');
  const [customers, setCustomers] = useState<ExternalCustomer[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<ExternalCustomer | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setExistingLoading(true);
    try {
      const junctions = await StudentPaymentAccountService.findAllByStudentIdWithAccount(student.id);
      const subChecks = await Promise.all(
        junctions.map(async (j) => {
          const sub = await StudentSubscriptionService.findLinkedByStudentJunction(student.id, j.id).catch(() => null);
          return [j.id, isStudentSubscriptionActive(sub)] as [number, boolean];
        })
      );
      setExistingAccounts(junctions);
      setJunctionSubMap(Object.fromEntries(subChecks));
    } catch {
      setExistingAccounts([]);
      setJunctionSubMap({});
    } finally {
      setExistingLoading(false);
    }
  }, [student.id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSystemChange = (system: PaymentSystem) => {
    setSelectedSystem(system);
    setCpfInput('');
    setCustomers(null);
    setSelectedCustomer(null);
    setSearchError(null);
    setSuccessMessage(null);
    setLinkError(null);
  };

  const handleSearch = async () => {
    const value = cpfInput.trim();
    if (!value) return;

    setSearching(true);
    setSearchError(null);
    setCustomers(null);
    setSelectedCustomer(null);
    setSuccessMessage(null);
    setLinkError(null);

    try {
      const data = await PaymentGatewayService.searchCustomers(selectedSystem, value);
      setCustomers(data);
    } catch {
      setSearchError('Não foi possível buscar as contas. Tente novamente.');
    } finally {
      setSearching(false);
    }
  };

  const handleConfirmUnlink = async () => {
    if (!junctionToUnlink) return;
    setUnlinking(true);
    setUnlinkError(null);
    try {
      await StudentPaymentAccountService.delete(junctionToUnlink.id);
      setJunctionToUnlink(null);
      await loadData();
    } catch {
      setUnlinkError('Erro ao desvincular conta. Tente novamente.');
    } finally {
      setUnlinking(false);
    }
  };

  const handleConfirmLink = async () => {
    if (!selectedCustomer) return;
    setConfirming(true);
    setLinkError(null);

    try {
      const paymentAccount = await PaymentAccountService.findOrCreateByExternalId(
        selectedSystem,
        selectedCustomer.id,
        { name: selectedCustomer.name, email: selectedCustomer.email ?? null }
      );

      const existingJunctions = await StudentPaymentAccountService.findAll({
        filters: [
          { column: 'student_id', value: student.id },
          { column: 'payment_account_id', value: paymentAccount.id },
        ],
      });

      if (existingJunctions.length === 0) {
        await StudentPaymentAccountService.linkToStudent(student.id, paymentAccount.id);
      }

      setSelectedCustomer(null);
      setSuccessMessage(`Conta "${selectedCustomer.name ?? ''}" vinculada com sucesso.`);
    } catch {
      setLinkError('Erro ao vincular conta. Tente novamente.');
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div className={styles['detail-header']}>
        <button type="button" className={styles['back-btn']} onClick={onBack}>
          <span className="material-symbols-outlined">arrow_back</span>
          Voltar
        </button>
        <div>
          <h2 className={styles['student-name']}>{student.full_name}</h2>
          {student.cpf && (
            <span className={styles['student-cpf']}>CPF: {maskCpf(student.cpf)}</span>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <p className={styles['results-label']}>CONTAS VINCULADAS</p>

      {existingLoading ? (
        <Loading />
      ) : existingAccounts.length === 0 ? (
        <EmptyState message="Nenhuma conta vinculada a esta aluna." icon="link_off" />
      ) : (
        <DataTable
          headers={['Sistema', 'ID externo', 'Nome', 'E-mail', 'Vinculado em', '']}
          gridTemplate="120px 1.5fr 1fr 1fr 140px 120px"
          borderedRows
          boldHeaders
        >
          {existingAccounts.map((j) => {
            const acc = j.payment_accounts;
            const hasActiveSub = junctionSubMap[j.id] ?? false;
            return (
              <DataTable.Row key={j.id}>
                <span>{PAYMENT_SYSTEM_LABELS[acc.payment_system] ?? acc.payment_system}</span>
                <span className={styles['cell-mono']}>{acc.external_customer_id}</span>
                <span>{acc.name ?? '—'}</span>
                <span>{acc.email ?? '—'}</span>
                <span>{formatDate(j.created_at)}</span>
                <div className={styles['actions-cell']}>
                  <Button
                    variant="ghost"
                    size="xs"
                    disabled={hasActiveSub}
                    title={hasActiveSub ? 'Esta conta não pode ser desvinculada pois tem uma assinatura.' : undefined}
                    onClick={() => setJunctionToUnlink(j)}
                  >
                    Desvincular
                  </Button>
                </div>
              </DataTable.Row>
            );
          })}
        </DataTable>
      )}

      <div className={styles.divider} />

      <div className={styles['search-section']}>
        <p className={styles['search-instruction']}>
          {selectedSystem === 'asaas'
            ? `Pesquise pelo CPF do titular para encontrar contas de pagamento disponíveis no ${PAYMENT_SYSTEM_LABELS[selectedSystem]}.`
            : `Pesquise pelo e-mail do titular para encontrar contas de pagamento disponíveis no ${PAYMENT_SYSTEM_LABELS[selectedSystem]}.`}
        </p>

        <div className={styles['search-row']}>
          <select
            className={styles['system-select']}
            value={selectedSystem}
            onChange={(e) => handleSystemChange(e.target.value as PaymentSystem)}
          >
            <option value="asaas">{PAYMENT_SYSTEM_LABELS.asaas}</option>
            <option value="stripe">{PAYMENT_SYSTEM_LABELS.stripe}</option>
            <option value="paypal">{PAYMENT_SYSTEM_LABELS.paypal}</option>
          </select>
          <div className={styles['search-input-wrap']}>
            <FormInput
              id="cpf-search"
              label={selectedSystem === 'asaas' ? 'CPF' : 'E-mail'}
              value={cpfInput}
              onChange={(e) => setCpfInput(e.target.value)}
              placeholder={selectedSystem === 'asaas' ? '000.000.000-00' : 'email@exemplo.com'}
            />
          </div>
          <Button
            variant="primary"
            size="small"
            className={styles['search-btn']}
            onClick={handleSearch}
            disabled={searching || !cpfInput.trim()}
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </Button>
        </div>

        {searchError && <p className={styles.error}>{searchError}</p>}
        {linkError && <p className={styles.error}>{linkError}</p>}
        {unlinkError && <p className={styles.error}>{unlinkError}</p>}
        {successMessage && <p className={styles.success}>{successMessage}</p>}
      </div>

      {customers !== null && (
        <>
          <div className={styles.divider} />
          <p className={styles['results-label']}>CONTAS ENCONTRADAS</p>

          {customers.length === 0 ? (
            <EmptyState
              message={selectedSystem === 'asaas'
                ? 'Nenhuma conta encontrada para este CPF.'
                : 'Nenhuma conta encontrada para este e-mail.'}
              icon="person_search"
            />
          ) : (
            <DataTable
              headers={['ID externo', 'Nome', 'CPF/CNPJ', 'E-mail', '']}
              gridTemplate="1.2fr 1.5fr 160px 1fr 120px"
              borderedRows
              boldHeaders
            >
              {customers.map((c) => (
                <DataTable.Row key={c.id}>
                  <span className={styles['cell-mono']}>{c.id}</span>
                  <span>{c.name ?? '—'}</span>
                  <span>{c.document ?? '—'}</span>
                  <span>{c.email ?? '—'}</span>
                  <div className={styles['actions-cell']}>
                    <Button variant="primary" size="xs" onClick={() => setSelectedCustomer(c)}>
                      Vincular
                    </Button>
                  </div>
                </DataTable.Row>
              ))}
            </DataTable>
          )}
        </>
      )}

      <ConfirmModal
        open={selectedCustomer !== null}
        title="Vincular conta de pagamento"
        message={`Deseja vincular a conta "${selectedCustomer?.name ?? ''}" (${selectedCustomer?.id ?? ''}) à aluna ${student.full_name}?`}
        confirmLabel="Vincular"
        cancelLabel="Cancelar"
        variant="info"
        loading={confirming}
        onConfirm={handleConfirmLink}
        onCancel={() => setSelectedCustomer(null)}
      />

      <ConfirmModal
        open={junctionToUnlink !== null}
        title="Desvincular conta de pagamento"
        message={`Deseja remover a associação da conta "${junctionToUnlink?.payment_accounts.name ?? junctionToUnlink?.payment_accounts.external_customer_id ?? ''}" com a aluna ${student.full_name}?`}
        confirmLabel="Desvincular"
        cancelLabel="Cancelar"
        variant="danger"
        loading={unlinking}
        onConfirm={handleConfirmUnlink}
        onCancel={() => setJunctionToUnlink(null)}
      />
    </>
  );
};

export default FinanceiroTab;
